/**
 * AG Charts + AG Grid ⇄ Cinch design-system glue (shared, reusable).
 *
 * Graduated from the Charts Lab eval into the DS component library so every
 * feature (Charts Lab, Reports, …) renders AG visuals with identical brand +
 * light/dark treatment. See the "Chart Design" section of the Design System
 * page for the living reference.
 *
 * Two things make AG different from our incumbent recharts:
 *   1. Both AG Charts and AG Grid are MODULAR — nothing renders unless the
 *      community modules are registered once at startup
 *      ({@link ensureAgChartsRegistered} / {@link ensureAgGridRegistered}).
 *   2. AG Charts paints to <canvas>, so — unlike recharts, whose SVG can
 *      reference `hsl(var(--cinch-brand))` and inherit our light/dark cascade
 *      for free — it needs concrete color strings resolved in JS. We resolve
 *      the same DS tokens via a throwaway DOM probe ({@link resolveToken}) and
 *      rebuild the theme whenever the `.dark` class flips ({@link useIsDark}).
 *      AG Grid's Theming API (v33+) is JS too, so it gets the same treatment.
 */
import { useEffect, useMemo, useState } from "react";

import { AllCommunityModule, ModuleRegistry, type AgChartTheme } from "ag-charts-community";
import {
  AllCommunityModule as AgGridAllCommunityModule,
  ModuleRegistry as AgGridModuleRegistry,
  colorSchemeDark,
  themeQuartz,
  type Theme as AgGridTheme,
} from "ag-grid-community";

// ─── module registration (must run before the first chart/grid renders) ──────
let chartsRegistered = false;
export function ensureAgChartsRegistered(): void {
  if (chartsRegistered) return;
  ModuleRegistry.registerModules([AllCommunityModule]);
  chartsRegistered = true;
}
ensureAgChartsRegistered();

let gridRegistered = false;
export function ensureAgGridRegistered(): void {
  if (gridRegistered) return;
  AgGridModuleRegistry.registerModules([AgGridAllCommunityModule]);
  gridRegistered = true;
}
ensureAgGridRegistered();

// ─── design-system color tokens ─────────────────────────────────────────────
/** Maps the DS color names used across the app (see kpi ProductArrTrends) to
 *  their CSS custom properties. */
export const COLOR_VAR = {
  brand: "--cinch-brand",
  power: "--cinch-power",
  purple: "--status-purple",
  teal: "--status-teal",
  orange: "--status-orange",
  success: "--status-success",
  warning: "--status-warning",
  danger: "--status-danger",
} as const;

export type CinchColor = keyof typeof COLOR_VAR;

/** Default series order when a dataset doesn't assign its own colors. */
export const DEFAULT_PALETTE_ORDER: CinchColor[] = [
  "brand",
  "power",
  "purple",
  "teal",
  "orange",
  "success",
  "warning",
  "danger",
];

/**
 * Resolve a CSS color expression to a concrete `rgb(...)` string the canvas
 * renderer accepts. Uses a hidden probe element so the full cascade (including
 * the active `.dark` overrides) is applied before we read it back.
 */
export function resolveCssColor(expr: string): string {
  if (typeof document === "undefined") return expr;
  const probe = document.createElement("span");
  probe.style.color = expr;
  probe.style.position = "absolute";
  probe.style.opacity = "0";
  probe.style.pointerEvents = "none";
  document.body.appendChild(probe);
  const resolved = getComputedStyle(probe).color;
  probe.remove();
  return resolved || expr;
}

/** Resolve a DS color token (e.g. "brand") to a concrete color for the canvas. */
export function resolveToken(name: CinchColor): string {
  return resolveCssColor(`hsl(var(${COLOR_VAR[name]}))`);
}

// ─── live light/dark awareness ──────────────────────────────────────────────
/** Tracks the `.dark` class on <html> so charts can re-theme when the app's
 *  theme toggles (mirrors the pattern in cinch/SignInParticles). */
export function useIsDark(): boolean {
  const [dark, setDark] = useState(
    () =>
      typeof document !== "undefined" &&
      document.documentElement.classList.contains("dark"),
  );
  useEffect(() => {
    const el = document.documentElement;
    const sync = () => setDark(el.classList.contains("dark"));
    const observer = new MutationObserver(sync);
    observer.observe(el, { attributes: true, attributeFilter: ["class"] });
    sync();
    return () => observer.disconnect();
  }, []);
  return dark;
}

// ─── AG Charts theme ─────────────────────────────────────────────────────────
/**
 * An AG Charts theme wired to the Cinch brand, given a series-aligned list of
 * already-resolved colors and the current mode. The `ag-default[-dark]` base
 * theme handles axis/label/gridline/tooltip colors for the mode; we only swap
 * the series palette to our brand colors.
 */
export function cinchAgTheme(colors: string[], isDark: boolean): AgChartTheme {
  return {
    baseTheme: isDark ? "ag-default-dark" : "ag-default",
    palette: { fills: colors, strokes: colors },
  };
}

/** Convenience: the default brand palette resolved for the current mode. */
export function useDefaultPalette(): { colors: string[]; isDark: boolean } {
  const isDark = useIsDark();
  const colors = useMemo(
    () => DEFAULT_PALETTE_ORDER.map(resolveToken),
    // resolveToken reads the live cascade; re-run when the mode flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isDark],
  );
  return { colors, isDark };
}

// ─── AG Grid theme (Theming API, v33+) ───────────────────────────────────────
/**
 * A brand-tinted Quartz theme for AG Grid in the given mode. Quartz already
 * handles the grid chrome; we set the accent (selection/focus) to Cinch brand,
 * inherit the app font, and layer the dark color-scheme part when dark. The
 * accent is resolved live so it tracks the DS token, not a hard-coded hex.
 */
export function cinchGridTheme(isDark: boolean): AgGridTheme {
  const base = isDark ? themeQuartz.withPart(colorSchemeDark) : themeQuartz;
  return base.withParams({
    accentColor: resolveToken("brand"),
    fontFamily: "inherit",
    headerFontWeight: 600,
    browserColorScheme: isDark ? "dark" : "light",
    wrapperBorderRadius: 8,
    headerBackgroundColor: isDark ? undefined : "var(--muted, #f4f6f8)",
  });
}

/** The Cinch AG Grid theme for the current mode, rebuilt on a theme flip. */
export function useCinchGridTheme(): AgGridTheme {
  const isDark = useIsDark();
  // resolveToken (inside cinchGridTheme) reads the live cascade, so isDark is
  // the only reactive input — rebuild when the mode flips.
  return useMemo(() => cinchGridTheme(isDark), [isDark]);
}
